import TeslaGiveawayDashboardView from '@/authenticated/components/TeslaGiveawayDashboardView'
import ActionableWinterWeatherAlertCard from '@/authenticated/components/WinterWeatherAlertCard/ActionableWinterWeatherAlertCard'
import InfoWinterWeatherAlertCard from '@/authenticated/components/WinterWeatherAlertCard/InfoWinterWeatherAlertCard'
import UtilityProgramLearnMoreDashboardView from '@/app/features/utility-programs/UtilityProgramLearnMoreDashboardView'
import { Vehicle } from '@/types/vehicle'
import TotalImpact from '@/authenticated/components/TotalImpact'
import TotalCost from '@/authenticated/components/TotalCost'
import Charges from '@/authenticated/components/Charges'
import PlugSessionsPage from '@/app/pages/devices/vehicle/PlugSessionsPage'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { BehindFlag, LockedFeature } from '@/app/components'
import ChargeForecastChart from '@/authenticated/components/ChargeForecast/ChargeForecastChart'
import ErrorBoundary, {
  LocationTag,
} from '@/authenticated/components/ErrorBoundary'
import ChargeForecastDashboardView from '@/authenticated/components/ChargeForecastDashboardView'
import InsightsCard from '@/app/components/Insights/InsightsCard'
import DeviceDREventAlertMessage from '@/app/features/vehicleDemandResponseEvent/DeviceDREventAlertMessage'
import ChargeForecastV2 from '@/app/features/charge-forecast-v2/ChargeForecastV2'
import { isChargeForecastV2PreviewOn } from '@/app/features/charge-forecast-v2/chargeForecastV2Preview'

export interface Props {
  vehicle?: Vehicle
  shouldHideActionableWinterWeatherAlert: boolean
  shouldHideInfoWinterWeatherAlert: boolean
}

export default function VehicleSection(props: Props) {
  // Percentage rollout keyed on user id (plug_sessions is an integer % in
  // remote config); experimental users are always in. Defaults to 0%.
  const showPlugSessions = useFeatureFlag({
    flag: 'plug_sessions',
    experimentalValue: true,
    rollout: true,
  })
  // Charge Forecast v2 replaces both the charge forecast card and the legacy
  // chart. Percentage rollout keyed on user id, like plug_sessions;
  // experimental users are always in. Defaults to 0%.
  const showChargeForecastV2 =
    useFeatureFlag({
      flag: 'charge_forecast_v2',
      experimentalValue: true,
      rollout: true,
    }) ||
    // Admin preview while impersonating; see chargeForecastV2Preview.ts
    isChargeForecastV2PreviewOn()

  if (!props.vehicle) {
    return null
  }

  return (
    <>
      <UtilityProgramLearnMoreDashboardView />
      <TeslaGiveawayDashboardView />

      <InsightsCard />
      <DeviceDREventAlertMessage
        deviceId={props.vehicle.id}
        deviceType="vehicle"
      />

      {!props.shouldHideActionableWinterWeatherAlert && (
        <ActionableWinterWeatherAlertCard />
      )}
      {!props.shouldHideInfoWinterWeatherAlert && (
        <InfoWinterWeatherAlertCard />
      )}

      {showChargeForecastV2 ? (
        <ErrorBoundary
          location={LocationTag.ChargeForecast}
          // Crash fallback reads "Something went wrong with the Charge
          // Forecast", same as the legacy card
          functionalityDescription="Charge Forecast"
          fallbackOnCard
        >
          <LockedFeature
            title="Charge Forecast"
            connectionRequirements={['home']}
          >
            <ChargeForecastV2 vehicleId={props.vehicle.id} />
          </LockedFeature>
        </ErrorBoundary>
      ) : (
        <>
          <BehindFlag flag="vehicle_chargeforecast" experimental>
            <ErrorBoundary
              location={LocationTag.VehicleChargerChargeForecastCard}
              functionalityDescription="Vehicle charger charge forecast card"
              fallbackOnCard
            >
              <ChargeForecastDashboardView vehicleId={props.vehicle.id} />
            </ErrorBoundary>
          </BehindFlag>

          <LockedFeature
            title="Charge Forecast"
            connectionRequirements={['utility', 'unpendingPlan']}
          >
            <ChargeForecastChart vehicleId={props.vehicle.id} />
          </LockedFeature>
        </>
      )}

      {/* The new Plug Sessions section REPLACES the legacy Total Spent +
          Charges cards for customers inside the rollout percentage */}
      {showPlugSessions ? (
        <PlugSessionsPage vehicleId={props.vehicle.id} />
      ) : (
        <>
          <TotalCost vehicleId={props.vehicle.id} />
          <Charges vehicle={props.vehicle} />
        </>
      )}

      <TotalImpact vehicle={props.vehicle} />
    </>
  )
}
